





































import { CreateElement, VNode, VNodeDirective } from 'vue';
import { Component, Prop, Inject } from 'vue-property-decorator';
import _throttle from 'lodash/throttle';
import _debounce from 'lodash/debounce';
import { SwExtendedVue } from '../sw-extended-vue';
import { SwIcon } from '../sw-icon';
import { SwScrollView, OverflowEvent } from '../sw-scroll-view';
import { SwTransitionExpand } from '../sw-transition';
import SwTable from './sw-table.vue'; // only for types
import SwTableRow from './sw-table-row.vue';
import SwTableExpandRow from './sw-table-expand-row.vue';
import SwTableExpandSlide from './sw-table-expand-slide.vue';
import SwTableRowInsertCaret from './sw-table-row-insert-caret.vue';
import SwTableGroupRow from './sw-table-group-row.vue';
import { utilService } from './util.service';
import {
  TableColumnConfig,
  TableEntry,
  SortedColumnInfo,
  TableBodyMetrics,
  TableRowClaimExpandEventData,
  TableColumnRenderFunc,
  TableRowExpandMode,
  TableEntryFloatActionsGenerator,
  PaginationConfig,
  ClaimedRowInsertCaret,
  TableData,
  TableEntryIndexInfo,
  TableEntryGroup,
  FloatActionPlacement,
} from './types';
import { CSSStyleOptions, LooseObject } from '../common-types';
import { domService } from '../dom-service';
import { drag, DragDirValues, DragPosition } from '../drag-dir';

const cssPrefix = 'sw-table-body';

const DATA_ATTR_RENDERED_INDEX = 'data-raw-rendered-row-index';
const VIRTUAL_SCROLL_PHASEOUT_MARGIN = 50; // rows

/**
 * @event `dom-updated`
 * @event `scroll`
 * @event overflow `(event: OverflowEvent)`
 */
@Component({
  components: {
    SwIcon,
    SwScrollView,
    SwTransitionExpand,
    SwTableRow,
    SwTableExpandRow,
    SwTableExpandSlide,
    SwTableRowInsertCaret,
    SwTableGroupRow,
  },
  directives: {
    drag,
  },
})
export default class SwTableBody extends SwExtendedVue {
  @Prop({ type: String, required: true })
  instanceId: string;

  @Prop({ type: Array, required: true })
  columnsConfig: TableColumnConfig[];

  @Prop({ type: Array, required: true })
  effectiveColumnsConfig: TableColumnConfig[];

  @Prop({ type: Array, required: true })
  tableData: TableData;

  @Prop({ type: String, required: true })
  expandMode: TableRowExpandMode;

  @Prop({ type: Boolean, required: true })
  accordion: boolean;

  @Prop({ type: Function })
  expandRender: TableColumnRenderFunc;

  @Prop({ type: Number, default: 2 })
  columnsToShowSlideExpand: number;

  @Prop({ type: Array, required: true })
  sortedColumns: SortedColumnInfo[];

  @Prop({ type: String, required: true })
  theme: SwTable['theme'];

  @Prop({ type: String, required: true })
  size: SwTable['size'];

  @Prop({ type: Boolean, required: true })
  stripe: boolean;

  @Prop({ type: Boolean, required: true })
  border: boolean;

  @Prop({ type: Number, required: true })
  nestLevel: number;

  @Prop({ type: Number, required: true })
  indentSize: number;

  @Prop({ type: Boolean, required: true })
  noBackground: boolean;

  @Prop({ type: Array, default() { return []; } })
  selectedArr: string[];

  @Prop({ type: Array, default() { return []; } })
  expandedArr: string[];

  @Prop({ type: Object, default: null })
  effectivePaginationConfig: PaginationConfig | null;

  @Prop({ type: Function })
  getFloatActions: TableEntryFloatActionsGenerator;

  @Prop({ type: Object, required: true })
  bodyMetrics: TableBodyMetrics;

  @Prop({ type: [Boolean, String], default: false })
  forFixedPortion: boolean | 'left' | 'right';

  @Prop({ type: Boolean, required: true })
  clickRowToExpand: boolean;

  @Prop({ type: Boolean, required: true })
  clickRowToSelect: boolean;

  @Prop({ type: Boolean, required: true })
  isLoadingData: boolean;

  @Prop({ type: Boolean, required: true })
  rowDraggable: boolean;

  @Prop({ type: Array, required: true })
  collapsedGroups: string[];

  @Prop({ type: Boolean, default: false })
  scrollbarAutoHide: boolean;

  @Prop({ type: String, required: true })
  floatActionPosition: FloatActionPlacement;

  @Prop({ type: Boolean, required: true })
  holdingSelecting: boolean;

  cssPrefix = cssPrefix;
  lastClaimedExpandData: TableRowClaimExpandEventData | null = null;
  cachedRowInsertCaretInfo: ClaimedRowInsertCaret | null = null;

  scrollFrameId: number = 0;
  throttleReportFrameId: number = 0;

  visibleRenderedIndexRange: { start: number; end: number; } = { start: 0, end: 0 };

  bodyContDragStartCoor: DragPosition = { x: -1, y: -1 };
  bodyContDragStartEl: HTMLElement | null = null;

  get withGrouping() {
    return !utilService.isTableEntryArray(this.tableData);
  }

  get rowStartIndex(): number {
    if (this.effectivePaginationConfig) {
      return (Math.max(this.effectivePaginationConfig.pageNumber - 1, 0) * this.effectivePaginationConfig.pageSize);
    }
    return 0;
  }

  get rowProps() {
    return {
      instanceId: this.instanceId,
      columnsConfig: this.columnsConfig,
      effectiveColumnsConfig: this.effectiveColumnsConfig,
      columnsStyle: this.effectiveColumnsConfig.map<CSSStyleOptions>((col, index, arr) => {
        return utilService.getColStyles(col, index, arr[index + 1]);
      }),
      accordion: this.accordion,
      theme: this.theme,
      size: this.size,
      stripe: this.stripe,
      border: this.border,
      nestLevel: this.nestLevel,
      indentSize: this.indentSize,
      bodyMetrics: this.bodyMetrics,
      noBackground: this.noBackground,
      forTreeTable: this.isForTreeTable,
      forFixedPortion: !!this.forFixedPortion,
      clickRowToExpand: this.clickRowToExpand,
      clickRowToSelect: this.clickRowToSelect,
      rowDraggable: this.rowDraggable,
      floatActionPosition: this.floatActionPosition,
    };
  }

  get expandSlideProp() {
    if (!this.lastClaimedExpandData) { return {}; }
    if (!this.shouldShowExpandedSlide()) { return {}; }
    /** calculate slide width and the indicator position */
    const rowRect = this.lastClaimedExpandData.el.getBoundingClientRect();
    const nthChild = this.columnsToShowSlideExpand < this.effectiveColumnsConfig.length ?
      Math.min(this.columnsToShowSlideExpand, this.effectiveColumnsConfig.length - 1) : Math.min(2, this.effectiveColumnsConfig.length - 1);
    const cellRect = Array.from(this.lastClaimedExpandData.el.children)
      .filter(e => (e.matches('div.sw-table-row__cell:not(.sw-table-row__cell--zero-col)')))[nthChild - 1]
      .getBoundingClientRect();
    const slideWidth = (this.$el as HTMLElement).offsetWidth - (cellRect.left + cellRect.width - rowRect.left);
    const indiPos = rowRect.top - this.$el.getBoundingClientRect().top + rowRect.height / 2;
    return {
      entry: this.lastClaimedExpandData.entry,
      index: this.lastClaimedExpandData.index,
      expandRender: this.expandRender,
      nestLevel: this.nestLevel,
      selected: this.isEntrySelected(this.lastClaimedExpandData.entry),
      expanded: true,
      width: slideWidth,
      indicatorPos: indiPos,
    };
  }

  get tableStyles(): CSSStyleOptions {
    return {
      minWidth: `${utilService.calcTableWidth(this.effectiveColumnsConfig)}px`,
    };
  }

  get isForTreeTable() {
    if (this.nestLevel > 0) { return true; }
    if (utilService.isTableEntryArray(this.tableData)) {
      return this.tableData.some(entry => (!!entry._children));
    }
    return this.tableData.some((group) => {
      return group.entries.some(entry => (!!entry._children));
    });
  }

  get physicalRenderIndexRange(): [number, number] {
    const startIndex = Math.max(0, this.visibleRenderedIndexRange.start - VIRTUAL_SCROLL_PHASEOUT_MARGIN);
    const endIndex = this.visibleRenderedIndexRange.end < Number.POSITIVE_INFINITY
    ? this.visibleRenderedIndexRange.end + VIRTUAL_SCROLL_PHASEOUT_MARGIN
    : Number.POSITIVE_INFINITY;

    return [startIndex, endIndex];
  }

  get throttleUpdateVisibleRenderedIndexRange() {
    return _debounce(
      this.updateVisibleRenderedIndexRange,
      350,
    );
  }

  get throttleUpdateSelectedSetOnDrag() {
    return _throttle(
      this.updateSelectedSetOnDrag,
      100,
    );
  }

  get rowHeight() {
    return  this.size === 'normal' ? 42 : 29;
  }

  @Inject()
  readonly claimBodyMetrics: SwTable['claimBodyMetrics'];

  @Inject()
  readonly claimMoveEntry: SwTable['claimMoveEntry'];

  @Inject()
  readonly claimEntrySetSelected: SwTable['claimEntrySetSelected'];

  @Inject()
  readonly claimMostRecentRegularSelectedEntry: SwTable['claimMostRecentRegularSelectedEntry'];

  private isRowExpandable = utilService.isRowExpandable;
  private calcEffectiveExpandMode = utilService.calcEffectiveExpandMode;

  private isEntrySelected(entry: TableEntry) {
    return this.selectedArr.indexOf(entry._key) >= 0;
  }

  private isEntryExpanded(entry: TableEntry) {
    return this.expandedArr.indexOf(entry._key) >= 0;
  }

  private calcRowExpandedState(entry: TableEntry): boolean {
    const effectiveExpandMode = this.calcEffectiveExpandMode(entry, this.expandMode);
    return this.isRowExpandable(this.effectiveColumnsConfig, entry, effectiveExpandMode, this.expandRender) && this.isEntryExpanded(entry);
  }

  private shouldShowExpandedRow(entry: TableEntry): boolean {
    const effectiveExpandMode = this.calcEffectiveExpandMode(entry, this.expandMode);
    return effectiveExpandMode === 'in-row'
      && this.isRowExpandable(this.effectiveColumnsConfig, entry, effectiveExpandMode, this.expandRender)
      && this.isEntryExpanded(entry);
  }

  private shouldShowExpandedSlide(): boolean {
    if (!this.lastClaimedExpandData) { return false; }
    const effectiveExpandMode = this.calcEffectiveExpandMode(this.lastClaimedExpandData.entry, this.expandMode);
    return effectiveExpandMode === 'slide'
      && this.isRowExpandable(this.effectiveColumnsConfig, this.lastClaimedExpandData.entry, effectiveExpandMode, this.expandRender)
      && this.isEntryExpanded(this.lastClaimedExpandData.entry);
  }

  private reportBodyMetrics(contComp: SwScrollView | null) {
    const toReport: Partial<TableBodyMetrics> = {};
    if (contComp) {
      toReport.top = contComp.scrollTop;
      if (!this.forFixedPortion) {
        Object.assign(toReport, {
          left: contComp.scrollLeft,
          width: contComp.scrollWidth,
          clientWidth: contComp.clientWidth,
          // NOTE: use the table height instead of scroll-view height as the min scroll-view height is 100%
          height: (this.$refs.bodyTable as HTMLElement).scrollHeight,
          clientHeight: contComp.clientHeight,
        } as TableBodyMetrics);
      }
    } else {
      toReport.top = null;
      toReport.left = null;
      toReport.width = null;
      toReport.clientWidth = null;
      toReport.height = null;
      toReport.clientHeight = null;
    }
    this.claimBodyMetrics(toReport);
  }

  private async reportBodyContMetrics(forceSyncSize: boolean = false) {
    if (!this.$refs.bodyCont) { return; }
    const bodyContComp = (this.$refs.bodyCont as SwScrollView);
    if (forceSyncSize) {
      // await bodyContComp.updateSizes();
    }
    this.reportBodyMetrics(bodyContComp);
  }

  private delayReportBodyMetrics() {
    if (this.throttleReportFrameId) { return; }
    this.throttleReportFrameId = window.requestAnimationFrame(() => {
      this.reportBodyContMetrics(true)
      .then(() => {
        this.throttleUpdateVisibleRenderedIndexRange();
      });
      this.throttleReportFrameId = 0;
    });
  }
  public shortDelayReportBodyMetrics() {
    this.delayReportBodyMetrics();
  }

  private getRowElementFromClientPoint(point: { x: number; y: number; }, onlyRegularRow: boolean = false): HTMLElement | null {
    const el = document.elementFromPoint(point.x, point.y);
    if (!el) { return null; }
    const queries: string[] = [`.sw-table-row--${this.instanceId}[${DATA_ATTR_RENDERED_INDEX}]`];
    if (!onlyRegularRow) {
      queries.push(
        `.sw-table-group-row--${this.instanceId}[${DATA_ATTR_RENDERED_INDEX}]`,
        `.sw-table-expand-row--${this.instanceId}[${DATA_ATTR_RENDERED_INDEX}]`,
      );
    }
    const rowEl = el.closest(queries.join(','));
    if (!rowEl) { return null; }
    return rowEl as HTMLElement;
  }

  private updateVisibleRenderedIndexRange() {
    const contEl = (this.$refs.bodyCont instanceof HTMLElement) ? (this.$refs.bodyCont) : (this.$refs.bodyCont as Vue).$el as HTMLElement;
    const tableHeight = (this.$refs.bodyTable as HTMLElement).offsetHeight;

    if (tableHeight <= contEl.clientHeight) {
      this.visibleRenderedIndexRange.start = 0;
      this.visibleRenderedIndexRange.end = Math.round(contEl.clientHeight / this.rowHeight);
      return;
    }

    const contRect = contEl.getBoundingClientRect();

    const firstEl = this.getRowElementFromClientPoint({ x: contRect.left + 2, y: contRect.top + 2 });
    const lastEl = this.getRowElementFromClientPoint({ x: contRect.left + 2, y: contRect.top + contEl.clientHeight - 2 });

    const firstIndex = (() => {
      if (firstEl) {
        return parseInt(firstEl.dataset['rawRenderedRowIndex'] || '', 10);
      }

      const firstRenderedEl = this.$el.querySelector(`${cssPrefix}__row-rendered-first--${this.instanceId}`) as HTMLElement;
      const lastRenderedEl = this.$el.querySelector(`${cssPrefix}__row-rendered-last--${this.instanceId}`) as HTMLElement;

      /** derive the firstIndex from the firstRenderedEl if possible */
      if (!firstRenderedEl) {
        return Math.floor(contEl.scrollTop / this.rowHeight);
      }

      const contTop = contEl.scrollTop;
      const firstRenderedTop = firstRenderedEl.offsetTop;
      const firstRenderedIndex = parseInt(firstRenderedEl.dataset['rawRenderedRowIndex'] || '', 10);

      if (contTop < firstRenderedTop) {
        return Math.max(0, firstRenderedIndex - Math.ceil((firstRenderedTop - contTop) / this.rowHeight));
      }

      /** derive the firstIndex from the lastRenderedEl if possible */
      if (!lastRenderedEl) {
        return Math.floor(contEl.scrollTop / this.rowHeight);
      }

      const lastRenderedTop = lastRenderedEl.offsetTop + lastRenderedEl.offsetHeight;
      const lastRenderedIndex = parseInt(lastRenderedEl.dataset['rawRenderedRowIndex'] || '', 10);

      if (contTop > lastRenderedTop) {
        return lastRenderedIndex + Math.ceil((contTop - lastRenderedTop) / this.rowHeight);
      }

      return Math.round((firstRenderedIndex + lastRenderedIndex) / 2);
    })();
    const lastIndex = lastEl
    ? parseInt(lastEl.dataset['rawRenderedRowIndex'] || '', 10)
    : Math.round(contEl.clientHeight / this.rowHeight) + firstIndex;

    this.visibleRenderedIndexRange.start = firstIndex;
    this.visibleRenderedIndexRange.end = lastIndex;
  }

  private onBodyScroll() {
    if (this.scrollFrameId) { return; }
    this.scrollFrameId = window.requestAnimationFrame(() => {
      this.reportBodyContMetrics()
      .then(() => {
        this.throttleUpdateVisibleRenderedIndexRange();
        this.$emit('scroll');
      });
      this.scrollFrameId = 0;
    });
  }

  private onResize() {
    if (this.forFixedPortion) { return; }
    if (!this.$refs.bodyCont) { return; }
    this.reportBodyMetrics(null);
    this.delayReportBodyMetrics();
  }

  private onContentDomUpdated() {
    this.reportBodyContMetrics()
      .then(() => {
        this.$emit('dom-updated');
      });
  }

  private onBodyOverflow(event: OverflowEvent) {
    this.reportBodyContMetrics()
      .then(() => {
        this.$emit('overflow', event);
      });
  }

  private onRowClaimExpand(data: TableRowClaimExpandEventData) {
    this.lastClaimedExpandData = data;
  }

  private onRowInsertCaretInfo(caretInfo: ClaimedRowInsertCaret) {
    if (!this.cachedRowInsertCaretInfo) {
      this.cachedRowInsertCaretInfo = caretInfo;
      return;
    }

    let regen = false;
    if (
      (!!this.cachedRowInsertCaretInfo.rowEl && !this.cachedRowInsertCaretInfo.rowEl.isSameNode(caretInfo.rowEl)) ||
      (!this.cachedRowInsertCaretInfo.rowEl && !!caretInfo.rowEl)
    ) {
      this.cachedRowInsertCaretInfo.rowEl = caretInfo.rowEl;
      regen = true;
    }
    if (this.cachedRowInsertCaretInfo.position !== caretInfo.position) {
      this.cachedRowInsertCaretInfo.position = caretInfo.position;
      regen = true;
    }
    if (regen) {
      this.cachedRowInsertCaretInfo = Object.assign({}, this.cachedRowInsertCaretInfo);
    }
  }

  private onRowRequestFinalizeInsert() {
    if (this.cachedRowInsertCaretInfo) {
      if (this.cachedRowInsertCaretInfo.rowEl) {
        const entryIndex = parseInt(this.cachedRowInsertCaretInfo.rowEl.dataset['rawEntryIndex'] || '', 10);
        const groupIndex = parseInt(this.cachedRowInsertCaretInfo.rowEl.dataset['rawGroupIndex'] || '', 10);

        if (!isNaN(groupIndex) && !isNaN(entryIndex)) {
          const groups = this.tableData as TableEntryGroup[];
          const group = groups[groupIndex];
          const entry: TableEntry | null = this.cachedRowInsertCaretInfo.position === 'before' ? group.entries[entryIndex] :
            (group.entries[entryIndex + 1] || null);
          if (entry !== undefined) {
            this.claimMoveEntry(this.cachedRowInsertCaretInfo.srcEntry, entry, group);
          }
        } else if (!isNaN(entryIndex)) {
          const entries = this.tableData as TableEntry[];
          const entry: TableEntry | null = this.cachedRowInsertCaretInfo.position === 'before' ? entries[entryIndex] :
            (entries[entryIndex + 1] || null);
          if (entry !== undefined) {
            this.claimMoveEntry(this.cachedRowInsertCaretInfo.srcEntry, entry);
          }
        }
      }
    }
    this.cachedRowInsertCaretInfo = null;
  }

  public updatePosition(pos: { top: number }) {
    (this.$refs.bodyCont as SwScrollView).updatePosition(pos.top, true);
  }

  private onDragBodyContStart(event: MouseEvent) {
    this.bodyContDragStartCoor = { x: event.clientX, y: event.clientY };
    this.bodyContDragStartEl = this.getRowElementFromClientPoint(this.bodyContDragStartCoor, true);
  }

  private onDragBodyContPositioning(toClientPos: DragPosition, event: MouseEvent) {// tslint:disable-line
    if (!this.bodyContDragStartEl) { return; }
    const currCoor: DragPosition = { x: this.bodyContDragStartCoor.x, y: event.clientY };
    this.throttleUpdateSelectedSetOnDrag(this.bodyContDragStartEl, currCoor);
  }

  private onDragBodyContDone(toClientPos: DragPosition, event: MouseEvent) {// tslint:disable-line
    if (this.bodyContDragStartEl) {
      const startIndexInfo: TableEntryIndexInfo = {
        groupIndex: this.bodyContDragStartEl.dataset['rawGroupIndex'] === undefined ? undefined : Number(this.bodyContDragStartEl.dataset['rawGroupIndex']),
        entryIndex: this.bodyContDragStartEl.dataset['rawEntryIndex'] === undefined ? -1 : Number(this.bodyContDragStartEl.dataset['rawEntryIndex']),
      };
      const entry: TableEntry = (() => {
        if (startIndexInfo.groupIndex !== undefined) {
          const group = this.tableData[startIndexInfo.groupIndex] as TableEntryGroup;
          return group.entries[startIndexInfo.entryIndex];
        }
        return this.tableData[startIndexInfo.entryIndex] as TableEntry;
      })();

      this.claimMostRecentRegularSelectedEntry(entry, event);
    }

    this.bodyContDragStartEl = null;
    this.bodyContDragStartCoor = { x: -1, y: -1 };
  }

  private updateSelectedSetOnDrag(startEl: HTMLElement, currCoor: DragPosition) {
    const currEl = this.getRowElementFromClientPoint(currCoor, true);
    if (!currEl || currEl.isEqualNode(startEl)) { return; }

    /** now get entries between startEl and currEl selected */
    const startIndexInfo: TableEntryIndexInfo = {
      groupIndex: startEl.dataset['rawGroupIndex'] === undefined ? undefined : Number(startEl.dataset['rawGroupIndex']),
      entryIndex: startEl.dataset['rawEntryIndex'] === undefined ? -1 : Number(startEl.dataset['rawEntryIndex']),
    };
    const currIndexInfo: TableEntryIndexInfo = {
      groupIndex: currEl.dataset['rawGroupIndex'] === undefined ? undefined : Number(currEl.dataset['rawGroupIndex']),
      entryIndex: currEl.dataset['rawEntryIndex'] === undefined ? -1 : Number(currEl.dataset['rawEntryIndex']),
    };
    const infos: TableEntryIndexInfo[] = (() => {
      if (
        (startIndexInfo.groupIndex !== undefined && startIndexInfo.groupIndex < currIndexInfo.groupIndex!) ||
        (startIndexInfo.groupIndex === undefined && startIndexInfo.entryIndex < currIndexInfo.entryIndex)
      ) {
        return [startIndexInfo, currIndexInfo];
      }
      return [currIndexInfo, startIndexInfo];
    })();

    let entries: TableEntry[] = [];
    if (infos[0].groupIndex !== undefined) {
      for (let i = infos[0].groupIndex; i <= infos[1].groupIndex!; i += 1) {
        const group = this.tableData[i] as TableEntryGroup;
        if (i === infos[0].groupIndex) {
          entries = entries.concat(group.entries.slice(infos[0].entryIndex));
        } else if (i === infos[1].entryIndex) {
          entries = entries.concat(group.entries.slice(0, infos[1].entryIndex + 1));
        } else {
          entries = entries.concat(group.entries);
        }
      }
    } else {
      entries = (this.tableData as TableEntry[]).slice(infos[0].entryIndex, infos[1].entryIndex + 1);
    }

    this.claimEntrySetSelected(entries);
  }

  private mounted() {
    this.$nextTick(() => {
      this.reportBodyContMetrics(true)
      .then(() => {
        this.throttleUpdateVisibleRenderedIndexRange();
      });
      domService.resizeObserver.on(this.$refs.bodyTable as HTMLElement, this.delayReportBodyMetrics);
    });

    window.addEventListener('resize', this.onResize);
  }

  private beforeDestroy() {
    this.throttleUpdateVisibleRenderedIndexRange.cancel();
    domService.resizeObserver.off(this.$refs.bodyTable as HTMLElement, this.delayReportBodyMetrics);
    window.removeEventListener('resize', this.onResize);
  }

  private renderDataRow(
    h: CreateElement, // tslint:disable-line
    entry: TableEntry,
    indexInfo: TableEntryIndexInfo,
    globalIndex: number, // index of the entry in the dataset across all groups
    /** index of all kinds of conceptually rendered rows (entry row and group row) across all groups, for virtual scroll purpose */
    globalRenderedRowIndex: number,
  ): VNode[] {
    const nodes: VNode[] = [];
    const attrs: LooseObject = Object.assign({}, {
      'data-raw-entry-index': indexInfo.entryIndex, // index of this entry in current group
      'data-raw-group-index': indexInfo.groupIndex, // index of this group in the dataset
      [DATA_ATTR_RENDERED_INDEX]: globalRenderedRowIndex,
    });

    nodes.push(
      <sw-table-row
        {...{ attrs }}
        class={[
          {
            [`${cssPrefix}__row-rendered-first--${this.instanceId}`]: globalRenderedRowIndex === this.physicalRenderIndexRange[0],
            [`${cssPrefix}__row-rendered-last--${this.instanceId}`]: globalRenderedRowIndex === this.physicalRenderIndexRange[1],
          },
        ]}
        key={entry._key}
        entry={entry}
        index={globalIndex + this.rowStartIndex}
        {...{ props: this.rowProps }}
        effective-expand-mode={this.calcEffectiveExpandMode(entry, this.expandMode)}
        expand-render={this.expandRender}
        selected={this.isEntrySelected(entry)}
        expanded={this.calcRowExpandedState(entry)}
        selectedArr={this.selectedArr}
        expandedArr={this.expandedArr}
        get-float-actions={this.getFloatActions}
        holding-selecting={this.holdingSelecting}
        on-claim-expand={this.onRowClaimExpand}
        on-insert-caret-info={this.onRowInsertCaretInfo}
        on-request-finalize-insert={this.onRowRequestFinalizeInsert}
      ></sw-table-row> as VNode,
    );

    if (this.shouldShowExpandedRow(entry)) {
      nodes.push(
        <sw-table-expand-row
          {...{ attrs }}
          key={`${entry._key}_expanded`}
          entry={entry}
          index={globalIndex + this.rowStartIndex}
          {...{ props: this.rowProps }}
          effectivePaginationConfig={this.effectivePaginationConfig}
        ></sw-table-expand-row> as VNode,
      );
    }

    return nodes;
  }

  private render(h: CreateElement): VNode {// tslint:disable-line
    const rowsNodes: VNode[] = [];

    if (this.isLoadingData) {
      rowsNodes.push(
        <sw-table-row
          entry={null}
          index={0}
          {...{ props: this.rowProps }}
          is-loading-data={true}
          effective-expand-mode={this.expandMode}
        ></sw-table-row> as VNode,
      );
    } else if (this.tableData.length <= 0) {
      rowsNodes.push(
        <sw-table-row
          entry={null}
          index={0}
          {...{ props: this.rowProps }}
          effective-expand-mode={this.expandMode}
        ></sw-table-row> as VNode,
      );
    } else {
      const skipRendering = (renderedIndex: number) => {
        return !(renderedIndex >= this.physicalRenderIndexRange[0] && renderedIndex <= this.physicalRenderIndexRange[1]);
      };
      let renderedRowIndex = 0;

      if (utilService.isTableEntryArray(this.tableData)) {
        for (let index  = 0; index < this.tableData.length; index += 1) {
          const entry = this.tableData[index];
          if (!skipRendering(renderedRowIndex)) {
            rowsNodes.push(...this.renderDataRow(h, entry, { entryIndex: index }, index, renderedRowIndex));
          }
          renderedRowIndex += 1;
        }
      } else {
        let globalIndex = 0;

        for (let gIndex = 0; gIndex < this.tableData.length; gIndex += 1) {
          const group = this.tableData[gIndex];
          const collapsed = this.collapsedGroups.includes(group.key);

          if (!skipRendering(renderedRowIndex)) {
            rowsNodes.push(
              <sw-table-group-row
                {...{ attrs: { [DATA_ATTR_RENDERED_INDEX]: renderedRowIndex } }}
                class={[
                  {
                    [`${cssPrefix}__row-rendered-first--${this.instanceId}`]: renderedRowIndex === this.physicalRenderIndexRange[0],
                    [`${cssPrefix}__row-rendered-last--${this.instanceId}`]: renderedRowIndex === this.physicalRenderIndexRange[1],
                  },
                ]}
                instance-id={this.instanceId}
                key={`group-${group.key}`}
                group={group}
                index={gIndex}
                collapsed={collapsed}
              /> as VNode,
            );
          }
          renderedRowIndex += 1;

          for (let eIndex  = 0; eIndex < group.entries.length; eIndex += 1) {
            const entry = group.entries[eIndex];
            if (!collapsed) {
              if (!skipRendering(renderedRowIndex)) {
                rowsNodes.push(...this.renderDataRow(h, entry, { groupIndex: gIndex, entryIndex: eIndex }, globalIndex, renderedRowIndex));
              }
              renderedRowIndex += 1;
            }
            globalIndex += 1;
          }
        }
      }

      if (this.physicalRenderIndexRange[0] > 0 && (renderedRowIndex - 1) >= this.physicalRenderIndexRange[0]) {
        rowsNodes.unshift(
          <div class={[`${cssPrefix}__leading-blank`]} style={{ height: `${this.physicalRenderIndexRange[0] * this.rowHeight}px` }}></div> as VNode,
        );
      }
      if (this.physicalRenderIndexRange[1] < renderedRowIndex - 1) {
        rowsNodes.push(
          <div class={[`${cssPrefix}__trailing-blank`]}
            style={{ height: `${(renderedRowIndex - 1 - this.physicalRenderIndexRange[1]) * this.rowHeight}px` }}
          ></div> as VNode,
        );
      }

      if (!!this.cachedRowInsertCaretInfo) {
        rowsNodes.push(
          <sw-table-row-insert-caret
            width={this.bodyMetrics.clientWidth}
            info={this.cachedRowInsertCaretInfo}
          /> as VNode,
        );
      }
    }

        // <sw-scroll-view
        //   class={[`${cssPrefix}__cont`, 'sw-flexbox__flex']}
        //   ref="bodyCont"
        //   scrollbar-theme={this.theme}
        //   scrollbar-auto-hide={this.scrollbarAutoHide || this.forFixedPortion === 'left'}
        //   on-scroll={this.onBodyScroll}
        //   on-content-dom-updated={this.onContentDomUpdated}
        //   on-overflow={this.onBodyOverflow}
        // >
        //   <div class={[`${this.cssPrefix}__cont__table`]} style={this.tableStyles} ref="bodyTable">
        //   { rowsNodes }
        //   </div>
        // </sw-scroll-view>

    const bodyContDirs: VNodeDirective[] = [];
    if (this.holdingSelecting) {
      bodyContDirs.push({
        name: 'drag',
        value: {
          start: this.onDragBodyContStart,
          positioning: this.onDragBodyContPositioning,
          done: this.onDragBodyContDone,
        } as DragDirValues,
      });
    }

    const rootNode = (
      <div class={[`${this.cssPrefix}`, `${this.cssPrefix}--${this.theme}`, 'sw-flexbox']}>
        <div
          class={[`${cssPrefix}__cont-native`, { [`${cssPrefix}__cont-native--holdable`]: this.holdingSelecting }, 'sw-flexbox__flex']}
          ref="bodyCont"
          {...{ directives: bodyContDirs }}
          on-scroll={this.onBodyScroll}
        >
          <div class={[`${this.cssPrefix}__cont__table`]} style={this.tableStyles} ref="bodyTable">
          { rowsNodes }
          </div>
        </div>
        <div class={[`${this.cssPrefix}__slide-cont`, 'sw-flexbox__flex-none']}>
          <sw-transition-expand direction="horizontal" set-overflow={false}>
          {
            this.shouldShowExpandedSlide() ? (<sw-table-expand-slide {...{ props: this.expandSlideProp }} />) : null
          }
          </sw-transition-expand>
        </div>
      </div> as VNode
    );

    return rootNode;
  }
}
